import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { format } from "date-fns";
import ReusableTable from "../../components/UI/ReusableTable";
import apiClient from "../../utils/apiUtils";
import Notification from "../../components/UI/Notification";
import DOMPurify from "dompurify";
import Modal from "../../components/UI/Modal";
import InterviewForm from "../../components/Form/InterviewForm";
import { AuthContext } from "../../contexts/AuthContext";
import {
  ArrowLeft,
  User,
  Users,
  CreditCard,
  ClipboardList,
  MapPin,
} from "lucide-react";
import { getRoleBasedRoute } from "../../utils/routeUtils";

const CampaignDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { roles, hasPermission } = useContext(AuthContext);
  const isHR = hasPermission("campaigns_update");

  const [campaign, setCampaign] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [checklist, setChecklist] = useState([]);
  const [technicalAssessments, setTechnicalAssessments] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [newChecklistItem, setNewChecklistItem] = useState("");
  const [newAssessment, setNewAssessment] = useState({ name: "", link: "" });
  const [assessmentErrors, setAssessmentErrors] = useState({
    name: "",
    link: "",
  });
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isInterviewModalOpen, setIsInterviewModalOpen] = useState(false);
  const [selectedCandidateForInterview, setSelectedCandidateForInterview] =
    useState(null);
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [selectedCandidateForAction, setSelectedCandidateForAction] =
    useState(null);
  const [actionType, setActionType] = useState(null);
  const [reasonText, setReasonText] = useState("");
  const [reasonError, setReasonError] = useState("");

  // Handle back navigation - check if we came from application responses
  const handleBackNavigation = () => {
    const fromApplicationResponses = location.state?.fromApplicationResponses;
    if (fromApplicationResponses) {
      navigate(getRoleBasedRoute("/application_form_response", roles));
    } else {
      navigate(getRoleBasedRoute("/campaigns", roles));
    }
  };

  // Fetch campaign details
  useEffect(() => {
    const fetchCampaignDetails = async () => {
      if (!id) {
        console.warn("No campaign ID provided");
        return;
      }

      try {
        setCampaignLoading(true);
        setCampaignError(null);
        console.log("Fetching campaign details for ID:", id);
        console.log("Full API path will be: /campaigns/" + id);

        const response = await apiClient.get(`/campaigns/${id}`);

        console.log("Campaign details response:", response);
        console.log("Response data:", response?.data);

        if (response?.data?.success && response?.data?.data) {
          console.log("Campaign data received:", response.data.data);
          setCampaign(response.data.data);
        } else {
          console.warn("Unexpected response format:", response?.data);
          throw new Error(
            response?.data?.message || "Failed to fetch campaign details",
          );
        }
      } catch (error) {
        console.error("Error fetching campaign details:", error);
        console.error("Error response:", error.response);
        console.error("Error status:", error.response?.status);
        console.error("Error URL:", error.config?.url);

        // Check if this is an auth error that should not trigger logout
        const isAuthError =
          error.response?.status === 401 || error.response?.status === 403;
        if (isAuthError) {
          console.log(
            "Auth error detected, but campaigns endpoint should not trigger logout",
          );
        }

        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to load campaign details";
        setCampaignError(errorMessage);
        setNotification({
          message: errorMessage,
          type: "error",
        });
        // Don't logout on error - let the user see the error message
      } finally {
        setCampaignLoading(false);
      }
    };

    fetchCampaignDetails();
  }, [id]);

  const fetchChecklist = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(`/checklists/campaign/${id}`);
      setChecklist(response.data?.data?.data || []);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setChecklist(null);
      } else {
        console.error("Error fetching checklists:", error);
      }
    }
  };

  const fetchAssessment = async () => {
    if (!id) return;
    try {
      const response = await apiClient.get(
        `/technical-assessments/campaign/${id}`,
      );
      setTechnicalAssessments(response.data?.data?.data || []);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setTechnicalAssessments(null);
      } else {
        console.error("Error fetching assessments:", error);
      }
    }
  };

  const getCandidateStatus = (candidate) => {
    return (
      candidate?.pivot_data?.status ||
      candidate?.pivot?.status ||
      candidate?.status ||
      "Pending"
    );
  };

  const getApplicationFormId = (candidate) => {
    return (
      candidate?.pivot_data?.application_form_id ||
      candidate?.pivot?.application_form_id ||
      candidate?.campaign?.application_form_id
    );
  };

  const getOrderedRounds = () => {
    if (!campaign?.round_type_mapping) return [];

    try {
      const mapping =
        typeof campaign.round_type_mapping === "string"
          ? JSON.parse(campaign.round_type_mapping)
          : campaign.round_type_mapping;

      if (Array.isArray(mapping)) {
        return mapping.map((roundType, index) => ({
          number: index + 1,
          type: roundType,
        }));
      }

      if (typeof mapping === "object" && mapping !== null) {
        return Object.keys(mapping)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map((key) => ({
            number: parseInt(key),
            type: mapping[key],
          }));
      }

      return [];
    } catch (error) {
      console.error("Error parsing round_type_mapping:", error);
      return [];
    }
  };

  const fetchCandidates = async (page = 1) => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await apiClient.get(
        `/candidates/campaign/${id}?page=${page}`,
      );

      setCandidates(response.data?.data?.data || []);
      setTotalPages(response.data?.data?.last_page || 1);
      setCurrentPage(response.data?.data?.current_page || 1);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setCandidates(null);
      } else {
        console.error("Error fetching candidates:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchCandidates(page);
    }
  };

  useEffect(() => {
    if (id && campaign) {
      fetchChecklist();
      fetchAssessment();
      fetchCandidates();
    }
  }, [id, campaign]);

  const addChecklistItem = async () => {
    if (!id) return;
    try {
      const response = await apiClient.post("/checklists", {
        campaign_id: id,
        name: newChecklistItem,
      });
      setNotification({
        message: "Item added to the checklist successfully",
        type: "success",
      });
      setChecklist((prev) =>
        Array.isArray(prev)
          ? [...prev, response.data?.data]
          : [response.data?.data],
      );
      setNewChecklistItem("");
    } catch (error) {
      setNotification({
        message: "Error adding checklist item",
        type: "error",
      });
    }
  };

  const addTechnicalAssessment = async () => {
    if (!id) return;
    setAssessmentErrors({ name: "", link: "" });

    try {
      const response = await apiClient.post("/technical-assessments", {
        campaign_id: id,
        name: newAssessment.name,
        link: newAssessment.link,
      });
      setNotification({
        message: "Item added to the technical assessment successfully",
        type: "success",
      });
      setTechnicalAssessments((prev) =>
        Array.isArray(prev)
          ? [...prev, response.data?.data]
          : [response.data?.data],
      );
      setNewAssessment({ name: "", link: "" });
      setAssessmentErrors({ name: "", link: "" });
    } catch (error) {
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const fieldErrors = {
          name: errors.name ? errors.name[0] : "",
          link: errors.link ? errors.link[0] : "",
        };
        setAssessmentErrors(fieldErrors);

        if (error.response.data.message) {
          setNotification({
            message: error.response.data.message,
            type: "error",
          });
        }
      } else {
        setNotification({
          message: `Error adding technical assessment: ${
            error.response?.data?.message || error.message
          }`,
          type: "error",
        });
      }
    }
  };

  const deleteTechnicalAssessment = async (assessmentId) => {
    if (!window.confirm("Are you sure you want to delete this assessment?")) {
      return;
    }

    try {
      await apiClient.delete(`/technical-assessments/${assessmentId}`);
      setNotification({
        message: "Assessment deleted successfully",
        type: "success",
      });
      setTechnicalAssessments((prev) =>
        prev.filter((assessment) => assessment.id !== assessmentId),
      );
    } catch (error) {
      setNotification({
        message: `Error deleting assessment: ${
          error.response?.data?.message || error.message
        }`,
        type: "error",
      });
    }
  };

  const handleShortlistCandidate = (candidate) => {
    setSelectedCandidateForAction(candidate);
    setActionType("Shortlisted");
    setReasonText("");
    setReasonError("");
    setIsReasonModalOpen(true);
  };

  const handleRejectCandidate = (candidate) => {
    setSelectedCandidateForAction(candidate);
    setActionType("Rejected");
    setReasonText("");
    setReasonError("");
    setIsReasonModalOpen(true);
  };

  const closeReasonModal = () => {
    setIsReasonModalOpen(false);
    setSelectedCandidateForAction(null);
    setActionType(null);
    setReasonText("");
    setReasonError("");
  };

  const handleReasonChange = (e) => {
    const value = e.target.value;
    setReasonText(value);
    if (value.length > 200) {
      setReasonError("Reason cannot exceed 200 characters");
    } else {
      setReasonError("");
    }
  };

  const handleSubmitCandidateStatus = async () => {
    if (!id) return;
    if (!reasonText.trim()) {
      setReasonError("Reason is required");
      return;
    }

    if (reasonText.length > 200) {
      setReasonError("Reason cannot exceed 200 characters");
      return;
    }

    try {
      await apiClient.post("/candidates/status", {
        id: selectedCandidateForAction.id,
        campaign_id: id,
        status: actionType,
        reason: reasonText.trim(),
      });

      setNotification({
        message: `Candidate ${actionType.toLowerCase()} successfully`,
        type: "success",
      });

      setCandidates((prevCandidates) =>
        prevCandidates.map((candidate) =>
          candidate.id === selectedCandidateForAction.id
            ? {
                ...candidate,
                pivot_data: {
                  ...candidate.pivot_data,
                  status: actionType,
                },
                pivot: {
                  ...candidate.pivot,
                  status: actionType,
                },
                status: actionType,
              }
            : candidate,
        ),
      );

      closeReasonModal();
    } catch (error) {
      setNotification({
        message: `Error updating candidate status: ${
          error.response?.data?.message || error.message
        }`,
        type: "error",
      });
    }
  };

  const openInterviewModal = (candidate) => {
    setSelectedCandidateForInterview(candidate);
    setIsInterviewModalOpen(true);
  };

  const closeInterviewModal = () => {
    setIsInterviewModalOpen(false);
    setSelectedCandidateForInterview(null);
  };

  const handleInterviewSubmit = async (interviewData) => {
    if (!id) return;
    try {
      await apiClient.post("/interviews", {
        ...interviewData,
        campaign_id: id,
        candidate_id: selectedCandidateForInterview.id,
      });

      setNotification({
        message: "Interview scheduled successfully",
        type: "success",
      });

      closeInterviewModal();
      fetchCandidates(currentPage);
    } catch (error) {
      setNotification({
        message: `Error scheduling interview: ${
          error.response?.data?.message || error.message
        }`,
        type: "error",
      });
    }
  };

  const renderTabContent = () => {
    if (!campaign) return null;

    switch (activeTab) {
      case "overview":
        return (
          <div className="p-4 w-full mx-auto space-y-4">
            <div className="bg-white dark:bg-gray-800 p-6 border border-gray-200 rounded-xl ">
              <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-3 flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Job Description
              </h5>
              <hr></hr>
              <div className="bg-white dark:bg-gray-700 rounded-xl p-6">
                <div
                  className="text-gray-700 dark:text-gray-300 leading-relaxed overflow-hidden"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(campaign.job_description, {
                      ALLOWED_TAGS: [
                        "p",
                        "br",
                        "strong",
                        "em",
                        "u",
                        "h1",
                        "h2",
                        "h3",
                        "h4",
                        "h5",
                        "h6",
                        "ul",
                        "ol",
                        "li",
                        "blockquote",
                        "code",
                        "pre",
                        "span",
                        "div",
                      ],
                      ALLOWED_ATTR: ["class", "style"],
                    }),
                  }}
                />
              </div>
            </div>

            {/* <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <div className="w-2 h-2 bg-orange-500 rounded-full mr-3"></div>
                Campaign Details
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex gap-x-4">
                  <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">
                    Duration:
                  </span>
                  <span className="text-gray-800 dark:text-gray-200">
                    From {campaign.from_date} to {campaign.to_date}
                  </span>
                </div>
                <div className="flex gap-x-4">
                  <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">
                    Workplace:
                  </span>
                  <span className="text-gray-800 dark:text-gray-200">
                    {campaign.workplace?.name || campaign.workplace}
                  </span>
                </div>
              </div>
            </div> */}

            {(() => {
              const rounds = getOrderedRounds();
              if (rounds.length > 0) {
                const getOrdinalSuffix = (num) => {
                  const j = num % 10;
                  const k = num % 100;
                  if (j === 1 && k !== 11) return "st";
                  if (j === 2 && k !== 12) return "nd";
                  if (j === 3 && k !== 13) return "rd";
                  return "th";
                };

                return (
                  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                      <div className="w-2 h-2 bg-pink-500 rounded-full mr-3"></div>
                      Interview Rounds
                    </h5>
                    <div className="space-y-3">
                      {rounds.map((round, index) => (
                        <div
                          key={index}
                          className="flex items-center p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="w-8 h-8 bg-pink-100 dark:bg-pink-900 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                            <span className="text-pink-600 dark:text-pink-300 font-semibold text-sm">
                              {round.number}
                            </span>
                          </div>
                          <div className="flex-1">
                            <span className="text-gray-600 dark:text-gray-400 font-medium">
                              {round.number}
                              {getOrdinalSuffix(round.number)} Interview:
                            </span>
                            <span className="text-gray-800 dark:text-gray-200 font-semibold ml-2">
                              {round.type}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-6 rounded-lg border border-blue-200 dark:border-gray-600">
              <h5 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></div>
                Campaign Information
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">
                    Created By:
                  </span>
                  <div className="text-gray-800 dark:text-gray-200">
                    <p className="font-semibold">{campaign.user?.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {campaign.user?.email}
                    </p>
                  </div>
                  <span className="text-gray-800 dark:text-gray-400 font-medium block mb-1 mt-4">
                    Public Link:
                  </span>
                  <span className="text-gray-800 dark:text-gray-200 font-semibold">
                    <Link
                      to={`/campaign/${campaign?.id}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline hover:underline-offset-4"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {campaign?.campaign_name}
                    </Link>
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">
                      Created At:
                    </span>
                    <span className="text-gray-800 dark:text-gray-200 text-sm">
                      {campaign.created_at
                        ? format(
                            new Date(campaign.created_at),
                            "dd MMM yyyy, hh:mm a",
                          )
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400 font-medium block mb-1">
                      Last Updated:
                    </span>
                    <span className="text-gray-800 dark:text-gray-200 text-sm">
                      {campaign.updated_at
                        ? format(
                            new Date(campaign.updated_at),
                            "dd MMM yyyy, hh:mm a",
                          )
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "summary":
        return (
          <div className="bg-white p-4 w-full mx-auto">
            <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">
                  Campaign Summary
                </h4>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Candidates
                    </p>
                    <p className="text-3xl font-semibold text-blue-600">
                      {candidates?.length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">👥</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Pending
                    </p>
                    <p className="text-3xl font-semibold text-yellow-600">
                      {candidates?.filter(
                        (c) => getCandidateStatus(c) === "Pending",
                      ).length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">⏳</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Shortlisted
                    </p>
                    <p className="text-3xl font-semibold text-green-600">
                      {candidates?.filter(
                        (c) => getCandidateStatus(c) === "Shortlisted",
                      ).length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">✅</span>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Rejected
                    </p>
                    <p className="text-3xl font-semibold text-red-600">
                      {candidates?.filter(
                        (c) => getCandidateStatus(c) === "Rejected",
                      ).length || 0}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">❌</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <h5 className="text-lg font-semibold text-gray-800 dark:text-white">
                  Candidate Status Breakdown
                </h5>
              </div>

              <div className="divide-y divide-gray-200 dark:divide-gray-600">
                {candidates?.filter((c) => getCandidateStatus(c) === "Pending")
                  .length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Pending Review (
                        {
                          candidates.filter(
                            (c) => getCandidateStatus(c) === "Pending",
                          ).length
                        }
                        )
                      </h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates
                        .filter((c) => getCandidateStatus(c) === "Pending")
                        .map((candidate, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-medium text-gray-800 dark:text-white">
                                {candidate.name}
                              </h6>
                              <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-full text-xs font-medium">
                                Pending
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {candidate.email}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {candidates?.filter(
                  (c) => getCandidateStatus(c) === "Shortlisted",
                ).length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Shortlisted (
                        {
                          candidates.filter(
                            (c) => getCandidateStatus(c) === "Shortlisted",
                          ).length
                        }
                        )
                      </h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates
                        .filter((c) => getCandidateStatus(c) === "Shortlisted")
                        .map((candidate, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-medium text-gray-800 dark:text-white">
                                {candidate.name}
                              </h6>
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                                Shortlisted
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {candidate.email}
                            </p>
                            {candidate.interviews?.some(
                              (interview) => interview.status === "Scheduled",
                            ) && (
                              <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                                📅 Interview Scheduled
                              </p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {candidates?.filter((c) => getCandidateStatus(c) === "Rejected")
                  .length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Rejected (
                        {
                          candidates.filter(
                            (c) => getCandidateStatus(c) === "Rejected",
                          ).length
                        }
                        )
                      </h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates
                        .filter((c) => getCandidateStatus(c) === "Rejected")
                        .map((candidate, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-medium text-gray-800 dark:text-white">
                                {candidate.name}
                              </h6>
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full text-xs font-medium">
                                Rejected
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {candidate.email}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {candidates?.filter(
                  (c) =>
                    !["Pending", "Shortlisted", "Rejected"].includes(
                      getCandidateStatus(c),
                    ),
                ).length > 0 && (
                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                      <h6 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Other Statuses (
                        {
                          candidates.filter(
                            (c) =>
                              !["Pending", "Shortlisted", "Rejected"].includes(
                                getCandidateStatus(c),
                              ),
                          ).length
                        }
                        )
                      </h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {candidates
                        .filter(
                          (c) =>
                            !["Pending", "Shortlisted", "Rejected"].includes(
                              getCandidateStatus(c),
                            ),
                        )
                        .map((candidate, index) => (
                          <div
                            key={index}
                            className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-medium text-gray-800 dark:text-white">
                                {candidate.name}
                              </h6>
                              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded-full text-xs font-medium">
                                {getCandidateStatus(candidate)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {candidate.email}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case "technical-assessment":
        return (
          <div className="bg-white p-3 w-full mx-auto space-y-4">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                <h4 className="text-xl font-semibold text-gray-800 dark:text-white">
                  Technical Assessment
                </h4>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-gray-600">
              {technicalAssessments === null ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🔍</span>
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    No technical assessments found for this campaign.
                  </p>
                </div>
              ) : technicalAssessments.length > 0 ? (
                <div className="space-y-2">
                  <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Assessments ({technicalAssessments.length})
                  </h5>
                  <ul className="space-y-3">
                    {technicalAssessments.map((assessment) => (
                      <li
                        key={assessment.id}
                        className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-purple-500 rounded-full mr-3 flex-shrink-0"></div>
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                              {assessment.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <a
                              href={assessment.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm font-medium"
                            >
                              <span className="mr-2">🔗</span>
                              Open Assessment
                            </a>
                            <button
                              onClick={() =>
                                deleteTechnicalAssessment(assessment.id)
                              }
                              className="inline-flex items-center justify-center w-9 h-9 bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 text-red-600 dark:text-red-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                              aria-label={`Delete ${assessment.name}`}
                              title="Delete Assessment"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-500 dark:text-gray-400">
                    Loading assessments...
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-gray-600">
              <h5 className="font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Add New Assessment
              </h5>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      value={newAssessment.name}
                      onChange={(e) => {
                        setNewAssessment({
                          ...newAssessment,
                          name: e.target.value,
                        });
                        if (assessmentErrors.name) {
                          setAssessmentErrors({
                            ...assessmentErrors,
                            name: "",
                          });
                        }
                      }}
                      placeholder="Assessment Name"
                      title="Assessment Name"
                      className={`w-full dark:bg-gray-700 dark:text-white dark:border-gray-600 border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        assessmentErrors.name
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-purple-500 focus:border-transparent"
                      }`}
                    />
                    {assessmentErrors.name && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {assessmentErrors.name}
                      </p>
                    )}
                  </div>
                  <div>
                    <input
                      type="text"
                      value={newAssessment.link}
                      onChange={(e) => {
                        setNewAssessment({
                          ...newAssessment,
                          link: e.target.value,
                        });
                        if (assessmentErrors.link) {
                          setAssessmentErrors({
                            ...assessmentErrors,
                            link: "",
                          });
                        }
                      }}
                      placeholder="Assessment Link (e.g., https://example.com)"
                      title="Assessment Link"
                      className={`w-full dark:bg-gray-700 dark:text-white dark:border-gray-600 border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 transition-all duration-200 ${
                        assessmentErrors.link
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300 focus:ring-purple-500 focus:border-transparent"
                      }`}
                    />
                    {assessmentErrors.link && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {assessmentErrors.link}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={addTechnicalAssessment}
                  disabled={
                    !newAssessment.name.trim() || !newAssessment.link.trim()
                  }
                  className={`w-full sm:w-auto px-5 py-2.5 rounded-lg text-white font-medium transition-all duration-200 ${
                    newAssessment.name.trim() && newAssessment.link.trim()
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                      : "bg-gray-400 cursor-not-allowed opacity-60"
                  }`}
                >
                  Add Assessment
                </button>
              </div>
            </div>
          </div>
        );
      case "candidates":
        return (
          <div className="bg-white p-2 mx-auto">
            <div className="flex justify-between items-center mb-6 pb-4 dark:border-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <h4 className="text-2xl font-semibold text-gray-800 dark:text-white">
                  Applicant
                </h4>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">
                  Loading candidates...
                </p>
              </div>
            ) : candidates?.length > 0 ? (
              <div className="bg-white overflow-hidden">
                <ReusableTable
                  data={candidates}
                  columns={(() => {
                    const baseColumns = [
                      { label: "Name", field: "name" },
                      { label: "Email", field: "email" },
                      {
                        label: "Resume",
                        field: "resume_path",
                        render: (row) => {
                          const resumeUrl =
                            row.resume_url ||
                            (row.resume_path
                              ? `https://hrmspayroll.prepstripe.com/storage/${row.resume_path}`
                              : null);
                          return resumeUrl ? (
                            <a
                              id={`resume-${row.id}`}
                              href={resumeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-300 rounded-lg text-sm font-medium transition-colors duration-200"
                            >
                              📄 View Resume
                            </a>
                          ) : (
                            <span className="text-gray-400 italic">NA</span>
                          );
                        },
                      },
                      {
                        label: "Interview Status",
                        field: "interviews",
                        render: (row) => {
                          const interviews = row.interviews || [];
                          const latestInterview =
                            interviews.length > 0
                              ? interviews[interviews.length - 1]
                              : null;
                          const status = latestInterview
                            ? latestInterview.status
                            : "Not Scheduled";

                          let bg = "bg-gray-100 dark:bg-gray-900";
                          let text = "text-gray-800 dark:text-gray-200";

                          if (status === "Scheduled") {
                            bg = "bg-blue-100 dark:bg-blue-900";
                            text = "text-blue-800 dark:text-blue-200";
                          } else if (status === "Completed") {
                            bg = "bg-green-100 dark:bg-green-900";
                            text = "text-green-800 dark:text-green-200";
                          } else if (
                            status === "Rejected" ||
                            status === "Cancelled"
                          ) {
                            bg = "bg-red-100 dark:bg-red-900";
                            text = "text-red-800 dark:text-red-200";
                          } else if (status === "InProgress") {
                            bg = "bg-yellow-100 dark:bg-yellow-900";
                            text = "text-yellow-800 dark:text-yellow-200";
                          }

                          return (
                            <span
                              className={`px-3 py-1 rounded-full text-sm font-medium ${bg} ${text}`}
                            >
                              {status}
                            </span>
                          );
                        },
                      },
                    ];

                    if (isHR) {
                      baseColumns.push({
                        label: "Status Actions",
                        field: "status_actions",
                        className: "w-48",
                        render: (row) => {
                          const status = getCandidateStatus(row);
                          if (status === "Pending") {
                            return (
                              <div className="flex justify-center items-center gap-2 w-full">
                                <button
                                  className="bg-gradient-to-r from-green-600 to-green-700 text-white px-3 py-2 rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm whitespace-nowrap"
                                  onClick={() => handleShortlistCandidate(row)}
                                  aria-label={`Shortlist ${row.name}`}
                                >
                                  ✓ Shortlist
                                </button>
                                <button
                                  className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-2 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm whitespace-nowrap"
                                  onClick={() => handleRejectCandidate(row)}
                                  aria-label={`Reject ${row.name}`}
                                >
                                  ✕ Reject
                                </button>
                              </div>
                            );
                          } else if (status === "Shortlisted") {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                  ✓ Shortlisted
                                </span>
                              </div>
                            );
                          } else if (status === "Rejected") {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                  ✕ Rejected
                                </span>
                              </div>
                            );
                          } else if (status === "Onboarded") {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-purple-100 text-purple-800 dark:text-red-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                  Onboarded
                                </span>
                              </div>
                            );
                          } else if (status === "Offer Accepted") {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-indigo-100 text-indigo-600 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                  Offer Accepted
                                </span>
                              </div>
                            );
                          } else {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg font-medium text-sm whitespace-nowrap min-w-[120px] text-center">
                                  {status}
                                </span>
                              </div>
                            );
                          }
                        },
                      });

                      baseColumns.push({
                        label: "Interview Actions",
                        field: "interview_actions",
                        className: "w-48",
                        render: (row) => {
                          const candidateStatus = getCandidateStatus(row);
                          const interviews = row.interviews || [];
                          const latestInterview =
                            interviews.length > 0
                              ? interviews[interviews.length - 1]
                              : null;
                          const interviewStatus = latestInterview
                            ? latestInterview.status
                            : "Not Scheduled";

                          if (candidateStatus === "Shortlisted") {
                            if (
                              interviewStatus === "Not Scheduled" ||
                              interviewStatus === "Completed" ||
                              interviewStatus === "Rejected"
                            ) {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <button
                                    className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-sm whitespace-nowrap"
                                    onClick={() => openInterviewModal(row)}
                                    aria-label={`Schedule Interview for ${row.name}`}
                                  >
                                    📅 Schedule Interview
                                  </button>
                                </div>
                              );
                            } else if (interviewStatus === "Scheduled") {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg font-medium text-sm whitespace-nowrap min-w-[180px] text-center">
                                    📅 Interview Scheduled
                                  </span>
                                </div>
                              );
                            } else {
                              return (
                                <div className="flex justify-center items-center w-full">
                                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[180px] text-center">
                                    {interviewStatus}
                                  </span>
                                </div>
                              );
                            }
                          } else if (candidateStatus === "Pending") {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[180px] text-center">
                                  Review Required
                                </span>
                              </div>
                            );
                          } else if (candidateStatus === "Rejected") {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[183px] text-center">
                                  Not Available
                                </span>
                              </div>
                            );
                          } else {
                            return (
                              <div className="flex justify-center items-center w-full">
                                <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-lg font-medium text-sm whitespace-nowrap min-w-[183px] text-center">
                                  Not Available
                                </span>
                              </div>
                            );
                          }
                        },
                      });
                    }

                    return baseColumns;
                  })()}
                  filterable={false}
                />

                <div className="flex justify-center items-center gap-4 p-6  dark:border-gray-600">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    ← Previous
                  </button>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Page
                    </span>
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-lg font-medium">
                      {currentPage}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      of {totalPages}
                    </span>
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Next →
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">👥</span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  No candidates available.
                </p>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  if (campaignLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading campaign details...
          </p>
        </div>
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">😔</div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
            {campaignError ? "Error Loading Campaign" : "Campaign Not Found"}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {campaignError || "The requested campaign could not be found."}
          </p>
          <button
            onClick={handleBackNavigation}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {location.state?.fromApplicationResponses
              ? "Back to Application Responses"
              : "Back to Campaigns"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-900">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="z-30 bg-white dark:border-gray-700 flex-shrink-0">
        <div className="p-4 sm:p-6 dark:bg-gray-800">
          <button
            onClick={handleBackNavigation}
            className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors mb-6"
            aria-label={
              location.state?.fromApplicationResponses
                ? "Back to application responses"
                : "Back to campaigns"
            }
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-base font-medium">
              {location.state?.fromApplicationResponses
                ? "Back to Application Responses"
                : "Back to Campaigns"}
            </span>
          </button>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                    {campaign.campaign_name?.substring(0, 2).toUpperCase() ||
                      "CP"}
                  </span>
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                      {campaign.campaign_name}
                    </h1>
                    <span
                      className={`px-3 py-1 rounded-md text-sm font-medium ${
                        campaign.status === "Close"
                          ? "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200"
                          : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200"
                      }`}
                    >
                      {campaign.status}
                    </span>
                  </div>

                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                    {campaign.position} •{" "}
                    {campaign.workplace?.name || campaign.workplace || "Remote"}
                  </p>

                  <div className="flex items-center gap-8 text-gray-700 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <span className="text-base">₹ {campaign.ctc}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <svg
                        className="w-5 h-5 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span className="text-base">
                        {campaign.from_date} to {campaign.to_date}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-right">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  Campaign ID
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-wide">
                  CMP{String(campaign.id).padStart(3, "0")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900">
          <div className="flex justify-start sm:justify-center items-center px-4 sm:px-0">
            <div className="flex flex-wrap gap-3 bg-transparent">
              {[
                { key: "overview", label: "Overview", icon: User },
                { key: "candidates", label: "Applicants", icon: Users },
                {
                  key: "technical-assessment",
                  label: "Tech Assessment",
                  icon: CreditCard,
                },
                { key: "summary", label: "Summary", icon: ClipboardList },
              ].map((tab) => {
                const isActive = activeTab === tab.key;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.key}
                    onClick={() => !tab.disabled && setActiveTab(tab.key)}
                    disabled={tab.disabled}
                    className={`flex items-center gap-2 px-4 sm:px-5 py-3 rounded-xl border text-sm sm:text-base font-semibold transition-all duration-200 ${
                      isActive
                        ? "border-blue-500 text-blue-700 bg-white "
                        : "border-gray-200 text-gray-600 bg-white hover:border-gray-300 hover:text-gray-900"
                    } ${tab.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-6">
        {renderTabContent()}
      </div>

      <Modal
        isOpen={isInterviewModalOpen}
        onRequestClose={closeInterviewModal}
        title="Schedule Interview"
      >
        {selectedCandidateForInterview && (
          <InterviewForm
            initialValues={{
              candidate_id: selectedCandidateForInterview.id,
              scheduled_at: "",
              status: "Scheduled",
              interview_type: "",
              interview_link: "",
              location: "",
              round_type: "",
            }}
            onSubmit={handleInterviewSubmit}
            closeModal={closeInterviewModal}
          />
        )}
      </Modal>

      <Modal
        isOpen={isReasonModalOpen}
        onRequestClose={closeReasonModal}
        title={`${actionType === "Shortlisted" ? "Shortlist" : "Reject"} Candidate`}
      >
        {selectedCandidateForAction && (
          <div className="space-y-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Candidate:{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {selectedCandidateForAction.name}
                </span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email:{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {selectedCandidateForAction.email}
                </span>
              </p>
            </div>

            <div>
              <label
                htmlFor="reason"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reason"
                value={reasonText}
                onChange={handleReasonChange}
                placeholder={`Enter reason for ${actionType === "Shortlisted" ? "shortlisting" : "rejecting"} this candidate...`}
                rows={4}
                maxLength={200}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all duration-200 dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
                  reasonError
                    ? "border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500 focus:border-transparent"
                }`}
              />
              <div className="flex justify-between items-center mt-2">
                <div>
                  {reasonError && (
                    <p className="text-sm text-red-500 dark:text-red-400">
                      {reasonError}
                    </p>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {reasonText.length}/200 characters
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={closeReasonModal}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg font-medium transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitCandidateStatus}
                disabled={!reasonText.trim() || reasonText.length > 200}
                className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
                  reasonText.trim() && reasonText.length <= 200
                    ? actionType === "Shortlisted"
                      ? "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-xl"
                      : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-lg hover:shadow-xl"
                    : "bg-gray-400 cursor-not-allowed opacity-60 text-white"
                }`}
              >
                Send
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CampaignDetailsPage;
